import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { FiLock, FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiCopy, FiCheck, FiEye, FiEyeOff, FiKey, FiChevronDown, FiChevronUp, FiAlertCircle, FiFolder } from 'react-icons/fi';

const CARD_COLORS = [
  { bg: 'rgba(37,99,235,0.08)', border: 'rgba(37,99,235,0.2)', accent: '#2563eb' },
  { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)', accent: '#10b981' },
  { bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.2)', accent: '#8b5cf6' },
  { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)', accent: '#f59e0b' },
  { bg: 'rgba(236,72,153,0.08)', border: 'rgba(236,72,153,0.2)', accent: '#ec4899' },
  { bg: 'rgba(6,182,212,0.08)', border: 'rgba(6,182,212,0.2)', accent: '#06b6d4' },
];

const DEFAULT_FIELDS = [
  { label: 'Username', value: '', type: 'text' },
  { label: 'Password', value: '', type: 'password' },
];


// Capitalise each word's first letter, preserve rest as typed
function toTitleCase(str) {
  return str.replace(/(?:^|\s)\S/g, c => c.toUpperCase());
}

function FieldRow({ field, index, onChange, onDelete, onTypeToggle }) {
  const [visible, setVisible] = useState(false);
  const isPassword = field.type === 'password';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, alignItems: 'center' }}>
      <input
        value={field.label}
        onChange={e => onChange(index, 'label', toTitleCase(e.target.value))}
        placeholder="Field Name"
        style={inputStyle}
      />
      <div style={{ position: 'relative' }}>
        <input
          type={isPassword && !visible ? 'password' : 'text'}
          value={field.value}
          onChange={e => onChange(index, 'value', e.target.value)}
          placeholder="Value"
          style={{ ...inputStyle, paddingRight: 32 }}
        />
        {isPassword && (
          <button type="button" onClick={() => setVisible(v => !v)}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'flex' }}>
            {visible ? <FiEyeOff size={14} /> : <FiEye size={14} />}
          </button>
        )}
      </div>
      <button type="button" onClick={() => onTypeToggle(index)}
        title={isPassword ? 'Switch to text' : 'Switch to password'}
        style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: isPassword ? 'rgba(37,99,235,0.08)' : 'var(--bg-input)', cursor: 'pointer', color: isPassword ? '#2563eb' : 'var(--text-muted)', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {isPassword ? <FiLock size={13} /> : <FiKey size={13} />}
      </button>
      <button type="button" onClick={() => onDelete(index)}
        style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.06)', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <FiTrash2 size={13} />
      </button>
    </div>
  );
}

function CredentialModal({ entry, existingNames, categories, onClose, onSave }) {
  const [companyName, setCompanyName] = useState(entry ? entry.company_name : '');
  const [category, setCategory] = useState(entry ? (entry.category || '') : '');
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [fields, setFields] = useState(entry ? entry.fields.map(f => ({ ...f })) : DEFAULT_FIELDS.map(f => ({ ...f })));
  const [saving, setSaving] = useState(false);

  const isNew = category === '__new__';
  const effectiveCategory = isNew ? newCategoryInput.trim() : category.trim();

  const handleFieldChange = (i, key, val) => setFields(fs => fs.map((f, idx) => idx === i ? { ...f, [key]: val } : f));
  const handleTypeToggle = (i) => setFields(fs => fs.map((f, idx) => idx === i ? { ...f, type: f.type === 'password' ? 'text' : 'password' } : f));
  const handleDeleteField = (i) => setFields(fs => fs.filter((_, idx) => idx !== i));
  const handleAddField = () => setFields(fs => [...fs, { label: '', value: '', type: 'text' }]);

  const trimmedName = companyName.trim();

  const isDuplicate = trimmedName &&
    existingNames.some(n => n.toLowerCase() === trimmedName.toLowerCase() && (!entry || n.toLowerCase() !== entry.company_name.toLowerCase()));

  const hasValidField = fields.some(f => f.label.trim() && f.value.trim());
  const canSave = trimmedName && hasValidField && !isDuplicate && (!isNew || newCategoryInput.trim());

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    await onSave({ company_name: trimmedName, category: effectiveCategory || null, fields: fields.filter(f => f.label.trim()) });
    setSaving(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 300 }}>
      <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
        style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 500, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{entry ? 'Edit Credential' : 'Add Credential'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
        </div>
        <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
          {/* Category */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">— No Category —</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
              <option value="__new__">+ New category…</option>
            </select>
            {isNew && (
              <input
                value={newCategoryInput}
                onChange={e => setNewCategoryInput(toTitleCase(e.target.value))}
                placeholder="Category name"
                autoFocus
                style={{ ...inputStyle, marginTop: 8 }}
              />
            )}
          </div>
          {/* Company Name */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Company / Service Name *</label>
            <input
              value={companyName}
              onChange={e => setCompanyName(toTitleCase(e.target.value))}
              placeholder="e.g. Anz Bank, Aws, Google"
              style={{ ...inputStyle, borderColor: isDuplicate ? '#dc2626' : undefined }}
              autoFocus={!entry}
            />
            {isDuplicate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, fontSize: 12, color: '#dc2626', fontWeight: 500 }}>
                <FiAlertCircle size={12} /> A credential with this name already exists
              </div>
            )}
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle}>Fields</label>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
              Click <FiLock size={10} style={{ verticalAlign: 'middle' }} /> to mark a field as password (hidden by default).
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {fields.map((field, i) => (
              <FieldRow key={i} field={field} index={i} onChange={handleFieldChange} onDelete={handleDeleteField} onTypeToggle={handleTypeToggle} />
            ))}
          </div>
          <button type="button" onClick={handleAddField}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px dashed var(--border)', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13 }}>
            <FiPlus size={14} /> Add Field
          </button>
        </div>
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Cancel</button>
          <motion.button whileTap={canSave ? { scale: 0.97 } : {}} onClick={handleSave} disabled={saving || !canSave}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: canSave ? '#2563eb' : 'var(--border)', color: canSave ? '#fff' : 'var(--text-muted)', cursor: canSave ? 'pointer' : 'default', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, opacity: saving ? 0.7 : 1 }}>
            <FiSave size={14} /> {saving ? 'Saving...' : 'Save'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function FieldDisplay({ field }) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const isPassword = field.type === 'password';

  const copy = () => {
    navigator.clipboard.writeText(field.value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 2 }}>{field.label}</div>
        <div style={{ fontSize: 14, color: 'var(--text-primary)', fontFamily: isPassword ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>
          {isPassword && !visible ? '••••••••' : (field.value || '—')}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        {isPassword && (
          <button onClick={() => setVisible(v => !v)}
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
            {visible ? <FiEyeOff size={13} /> : <FiEye size={13} />}
          </button>
        )}
        {field.value && (
          <button onClick={copy}
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', cursor: 'pointer', color: copied ? '#10b981' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600 }}>
            {copied ? <><FiCheck size={12} /> Copied</> : <FiCopy size={12} />}
          </button>
        )}
      </div>
    </div>
  );
}

function CredentialCard({ entry, onEdit, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const color = CARD_COLORS[entry.id % CARD_COLORS.length];
  const passwordCount = entry.fields.filter(f => f.type === 'password').length;

  return (
    <motion.div layout style={{ background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)', overflow: 'hidden', position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color.accent }} />
      <div style={{ padding: '18px 16px 14px', cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: color.bg, border: `1px solid ${color.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FiLock size={17} color={color.accent} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.company_name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              {entry.fields.length} field{entry.fields.length !== 1 ? 's' : ''}{passwordCount > 0 ? ` · ${passwordCount} password${passwordCount !== 1 ? 's' : ''}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button onClick={e => { e.stopPropagation(); onMoveUp && onMoveUp(entry); }}
                disabled={isFirst}
                style={{ padding: '3px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-input)', cursor: isFirst ? 'default' : 'pointer', color: isFirst ? 'var(--text-muted)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', opacity: isFirst ? 0.3 : 1 }}>
                <FiChevronUp size={11} />
              </button>
              <button onClick={e => { e.stopPropagation(); onMoveDown && onMoveDown(entry); }}
                disabled={isLast}
                style={{ padding: '3px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-input)', cursor: isLast ? 'default' : 'pointer', color: isLast ? 'var(--text-muted)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', opacity: isLast ? 0.3 : 1 }}>
                <FiChevronDown size={11} />
              </button>
            </div>
            <button onClick={e => { e.stopPropagation(); onEdit(entry); }}
              style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
              <FiEdit2 size={13} />
            </button>
            <button onClick={e => { e.stopPropagation(); onDelete(entry); }}
              style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.06)', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center' }}>
              <FiTrash2 size={13} />
            </button>
            <div style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', marginLeft: 2 }}>
              {expanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
            </div>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            style={{ overflow: 'hidden', borderTop: '1px solid var(--border)' }}>
            <div style={{ padding: '12px 16px' }}>
              {entry.fields.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0' }}>No fields</div>
              ) : (
                entry.fields.map((field, i) => (
                  <FieldDisplay key={i} field={field} />
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const INACTIVITY_TIMEOUT = 3 * 60; // 3 minutes in seconds
const COUNTDOWN_START = 30; // show warning for last 30 seconds

export default function Credentials() {
  const { authFetch } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [toast, setToast] = useState(null);

  // Vault lock state — unlocked for session only
  const [locked, setLocked] = useState(() => sessionStorage.getItem('vault_unlocked') !== '1');
  const [vaultPassword, setVaultPassword] = useState('');
  const [vaultError, setVaultError] = useState('');
  const [vaultChecking, setVaultChecking] = useState(false);

  // Inactivity timeout
  const [countdown, setCountdown] = useState(null); // null = inactive warning off
  const inactivityTimer = useRef(null);
  const countdownInterval = useRef(null);

  const lockVault = useCallback(() => {
    sessionStorage.removeItem('vault_unlocked');
    setLocked(true);
    setCountdown(null);
    clearTimeout(inactivityTimer.current);
    clearInterval(countdownInterval.current);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (locked) return;
    setCountdown(null);
    clearTimeout(inactivityTimer.current);
    clearInterval(countdownInterval.current);
    inactivityTimer.current = setTimeout(() => {
      // Start countdown
      let remaining = COUNTDOWN_START;
      setCountdown(remaining);
      countdownInterval.current = setInterval(() => {
        remaining -= 1;
        if (remaining <= 0) {
          clearInterval(countdownInterval.current);
          lockVault();
        } else {
          setCountdown(remaining);
        }
      }, 1000);
    }, (INACTIVITY_TIMEOUT - COUNTDOWN_START) * 1000);
  }, [locked, lockVault]);

  // Set up activity listeners when vault is unlocked
  useEffect(() => {
    if (locked) return;
    resetInactivityTimer();
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, resetInactivityTimer, { passive: true }));
    return () => {
      events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
      clearTimeout(inactivityTimer.current);
      clearInterval(countdownInterval.current);
    };
  }, [locked, resetInactivityTimer]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const handleUnlock = async (e) => {
    e.preventDefault();
    setVaultChecking(true);
    setVaultError('');
    try {
      const res = await authFetch('/api/auth/verify-password', { method: 'POST', body: JSON.stringify({ password: vaultPassword }) });
      if (res.ok) {
        sessionStorage.setItem('vault_unlocked', '1');
        setLocked(false);
      } else {
        const data = await res.json().catch(() => ({}));
        setVaultError(data.error || 'Incorrect password');
      }
    } catch {
      setVaultError('Unable to verify password');
    } finally {
      setVaultChecking(false);
    }
  };

  useEffect(() => {
    if (locked) return;
    authFetch('/api/credentials').then(r => r.ok ? r.json() : []).then(data => {
      setCredentials(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [locked]);

  const handleSave = async ({ company_name, category, fields }) => {
    if (modal?.mode === 'edit') {
      const res = await authFetch(`/api/credentials/${modal.entry.id}`, { method: 'PUT', body: JSON.stringify({ company_name, category, fields }) });
      if (res.ok) {
        const updated = await res.json();
        setCredentials(prev => prev.map(c => c.id === modal.entry.id ? updated : c));
        showToast('Credential updated');
      }
    } else {
      const res = await authFetch('/api/credentials', { method: 'POST', body: JSON.stringify({ company_name, category, fields }) });
      if (res.ok) {
        const created = await res.json();
        setCredentials(prev => [...prev, created]);
        showToast('Credential saved');
      }
    }
    setModal(null);
  };

  const handleDelete = (entry) => {
    setConfirmDialog({
      title: 'Delete Credential',
      rows: [{ label: 'Entry', value: entry.company_name }],
      confirmLabel: 'Delete', confirmColor: '#dc2626',
      onConfirm: async () => {
        const res = await authFetch(`/api/credentials/${entry.id}`, { method: 'DELETE' });
        if (res.ok) { setCredentials(prev => prev.filter(c => c.id !== entry.id)); showToast('Credential deleted'); }
        setConfirmDialog(null);
      }
    });
  };

  // Categories derived from existing credentials (unique, sorted)
  const categories = useMemo(() => {
    const cats = [...new Set(credentials.map(c => c.category).filter(Boolean))];
    return cats.sort((a, b) => a.localeCompare(b));
  }, [credentials]);

  // Reorder handlers
  const handleMoveUp = async (entry) => {
    const idx = credentials.findIndex(c => c.id === entry.id);
    if (idx <= 0) return;
    const newCreds = [...credentials];
    [newCreds[idx - 1], newCreds[idx]] = [newCreds[idx], newCreds[idx - 1]];
    const withOrder = newCreds.map((c, i) => ({ ...c, sort_order: i }));
    setCredentials(withOrder);
    await authFetch('/api/credentials/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ order: withOrder.map(c => ({ id: c.id, sort_order: c.sort_order })) })
    });
  };

  const handleMoveDown = async (entry) => {
    const idx = credentials.findIndex(c => c.id === entry.id);
    if (idx < 0 || idx >= credentials.length - 1) return;
    const newCreds = [...credentials];
    [newCreds[idx], newCreds[idx + 1]] = [newCreds[idx + 1], newCreds[idx]];
    const withOrder = newCreds.map((c, i) => ({ ...c, sort_order: i }));
    setCredentials(withOrder);
    await authFetch('/api/credentials/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ order: withOrder.map(c => ({ id: c.id, sort_order: c.sort_order })) })
    });
  };

  // Live search: auto title-case as user types
  const handleSearchChange = (e) => {
    setSearch(toTitleCase(e.target.value));
  };

  // Filter by search (company name), then group by category (preserving sort_order within group)
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? credentials.filter(c => c.company_name?.toLowerCase().includes(q))
      : [...credentials];

    // Group: entries with a category go under that category, others under uncategorised
    const map = {};
    for (const c of filtered) {
      const cat = c.category || '';
      if (!map[cat]) map[cat] = [];
      map[cat].push(c);
    }

    // Sort category keys: named categories A-Z, uncategorised last
    const cats = Object.keys(map).sort((a, b) => {
      if (!a && b) return 1;
      if (a && !b) return -1;
      return a.localeCompare(b);
    });

    return cats.map(cat => ({ cat, entries: map[cat] }));
  }, [credentials, search]);

  const totalFiltered = grouped.reduce((s, g) => s + g.entries.length, 0);
  const existingNames = useMemo(() => credentials.map(c => c.company_name), [credentials]);

  if (locked) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: '40px 36px', width: '100%', maxWidth: 360, textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(37,99,235,0.1)', border: '1px solid rgba(37,99,235,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <FiLock size={24} style={{ color: '#2563eb' }} />
          </div>
          <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>Credential Vault</h2>
          <p style={{ margin: '0 0 24px', fontSize: 13, color: 'var(--text-secondary)' }}>Enter your password to unlock</p>
          <form onSubmit={handleUnlock} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              type="password"
              value={vaultPassword}
              onChange={e => { setVaultPassword(e.target.value); setVaultError(''); }}
              placeholder="Your password"
              autoFocus
              style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${vaultError ? '#dc2626' : 'var(--border)'}`, fontSize: 14, background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none' }}
            />
            {vaultError && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#dc2626', fontSize: 13 }}>
                <FiAlertCircle size={14} /> {vaultError}
              </div>
            )}
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit"
              disabled={vaultChecking || !vaultPassword}
              style={{ padding: '10px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 600, cursor: vaultChecking || !vaultPassword ? 'not-allowed' : 'pointer', opacity: vaultChecking || !vaultPassword ? 0.6 : 1 }}>
              {vaultChecking ? 'Verifying…' : 'Unlock'}
            </motion.button>
          </form>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <div className="skeleton" style={{ width: 160, height: 28, marginBottom: 8 }} />
        <div className="skeleton" style={{ width: 220, height: 14, marginBottom: 24 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: 90, borderRadius: 14 }} />)}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Inactivity countdown overlay */}
      <AnimatePresence>
        {countdown !== null && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }}
              style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '32px 36px', textAlign: 'center', maxWidth: 340, border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
              <FiLock size={32} style={{ color: '#f59e0b', marginBottom: 12 }} />
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Vault Auto-Locking</h3>
              <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-secondary)' }}>
                No activity detected. Locking in <strong style={{ color: countdown <= 10 ? '#ef4444' : '#f59e0b', fontSize: 20 }}>{countdown}s</strong>
              </p>
              <button onClick={resetInactivityTimer}
                style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Stay Unlocked
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            style={{ position: 'fixed', top: 20, right: 20, zIndex: 999, padding: '12px 20px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', fontSize: 13, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiCheck size={16} /> {toast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>Credential Vault</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-secondary)' }}>
            {credentials.length} saved {credentials.length === 1 ? 'entry' : 'entries'} · encrypted at rest
          </p>
        </div>
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={() => setModal({ mode: 'add' })}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, boxShadow: '0 2px 8px rgba(37,99,235,0.3)' }}>
          <FiPlus size={16} /> Add Credential
        </motion.button>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 20 }}>
        <FiSearch size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
        <input value={search} onChange={handleSearchChange}
          placeholder="Search company or service name..."
          style={{ width: '100%', padding: '10px 12px 10px 36px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 14, outline: 'none', background: 'var(--bg-card)', boxSizing: 'border-box', color: 'var(--text-primary)' }} />
      </div>

      {/* Grouped Cards */}
      {grouped.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <FiLock size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 15 }}>{search ? 'No matching credentials' : 'No credentials saved yet'}</div>
          {!search && <div style={{ fontSize: 13, marginTop: 6 }}>Click "Add Credential" to store your first entry.</div>}
        </div>
      ) : (
        grouped.map(({ cat, entries }) => (
          <div key={cat || '__none__'} style={{ marginBottom: 28 }}>
            {/* Category header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <FiFolder size={14} color="var(--text-muted)" />
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {cat || 'Uncategorised'}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-input)', borderRadius: 10, padding: '1px 7px', fontWeight: 600 }}>
                {entries.length}
              </span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)', marginLeft: 4 }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
              {entries.map((entry, ei) => (
                <CredentialCard key={entry.id} entry={entry}
                  onEdit={e => setModal({ mode: 'edit', entry: e })}
                  onDelete={handleDelete}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  isFirst={credentials.indexOf(entry) === 0}
                  isLast={credentials.indexOf(entry) === credentials.length - 1} />
              ))}
            </div>
          </div>
        ))
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {modal && (
          <CredentialModal
            entry={modal.mode === 'edit' ? modal.entry : null}
            existingNames={existingNames}
            categories={categories}
            onClose={() => setModal(null)}
            onSave={handleSave}
          />
        )}
      </AnimatePresence>

      {/* Confirm Dialog */}
      <AnimatePresence>
        {confirmDialog && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setConfirmDialog(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 300 }}>
            <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 440, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' }}>
              <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{confirmDialog.title}</h3>
                <button onClick={() => setConfirmDialog(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: '18px 22px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                  {confirmDialog.rows.map((row, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                      <span style={{ fontWeight: 600, color: 'var(--text-secondary)', minWidth: 110, flexShrink: 0 }}>{row.label}</span>
                      <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{row.value}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => setConfirmDialog(null)}
                    style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                    Cancel
                  </button>
                  <motion.button whileTap={{ scale: 0.97 }} onClick={confirmDialog.onConfirm}
                    style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: confirmDialog.confirmColor || '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {confirmDialog.confirmLabel || 'Confirm'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
  marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.3px'
};

const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
  fontSize: 13, outline: 'none', boxSizing: 'border-box', background: 'var(--bg-input)',
  fontFamily: 'inherit', color: 'var(--text-primary)'
};
